import { SYSTEM_PROMPT, CACHED_REFERENCE } from './prompt';
import { listToolSchemas } from './toolSchemas';
import { getAiSettings, providerMeta } from './aiSettings';
import { getAiKey, AiKeyVaultError } from './keyStore';
import { resolveAiTarget } from './providerResolver';
import { noteAiTurnTarget } from './aiAuthorship';
import * as anthropic from './providers/anthropic';
import * as openai from './providers/openai';
import * as gemini from './providers/gemini';
import * as openrouter from './providers/openrouter';
import * as apiboo from './providers/apiboo';

/**
 * The AI engine — what replaced the Phoenix "ai:<session>" channel
 * (services/aiChannelService.js, retired 16.08.2026 with the backend proxy).
 *
 * Same callback surface the panel already consumed from the channel
 * (onTextDelta / onToolUse / onUsage / onEndTurn / onError), no quota events
 * (server metering is gone — the user pays their own provider directly), and
 * the system prompt + tool schemas now live client-side (prompt.js /
 * toolSchemas.js — the canonical copies).
 *
 * Providers are selected per-turn from settings so a mid-conversation
 * provider switch just works: the history is Anthropic-shaped regardless of
 * provider, and each adapter translates it (see providers/*).
 */

const PROVIDERS = { anthropic, openai, gemini, openrouter, apiboo };

/**
 * Can a turn run right now?
 * Resolves the providerResolver target — { state, provider, model, via,
 * fallbackFrom, apiboo, selectedProvider } — with state one of:
 *   'ready'        — a BYO key is readable, OR the Apiboo assistant stands in
 *                    (via:'apiboo'; fallbackFrom says why)
 *   'no_key'       — no key for the active provider and no Apiboo assistant
 *   'vault_locked' — vault locked or not set up, and no Apiboo assistant
 *   'no_vault'     — vault unreachable (not under Tauri), and no Apiboo assistant
 */
export async function checkAiReadiness() {
  return resolveAiTarget();
}

/** The typed refusal for a target that is not ready — the messages the panel already shows. */
function notReadyError(state) {
  if (state === 'vault_locked') {
    return new AiKeyVaultError('vault_locked', 'Unlock your vault to use your AI key.');
  }
  if (state === 'no_vault') {
    return new AiKeyVaultError(
      'vault_unavailable',
      'The vault is not available — AI keys are stored in your encrypted vault.'
    );
  }
  return new AiKeyVaultError(
    'no_key',
    'No API key saved for this provider — add one under Settings → AI Chat.'
  );
}

/**
 * Run one model turn against the resolved provider — the user's own key, or
 * the Apiboo assistant when no usable key exists (services/ai/providerResolver.js).
 *
 * `messages` is the Anthropic-shaped history the panel maintains. Callbacks
 * mirror the retired channel protocol exactly; exactly one of
 * onEndTurn/onError fires last. Returns an abort function — after calling it
 * no further callbacks fire. On the streaming path it also aborts the Rust
 * task, which closes the connection and actually stops the provider; on the
 * buffered path the request cannot be cancelled mid-flight, so its result is
 * discarded instead.
 */
export function runAiTurn(messages, { onTextDelta, onToolUse, onUsage, onEndTurn, onError } = {}) {
  let aborted = false;
  let cancelStream = null;

  (async () => {
    try {
      const target = await resolveAiTarget();
      if (target.state !== 'ready') throw notReadyError(target.state);
      // AI Act Art. 50(2): what this turn's tool writes record as their
      // provider and model (services/ai/aiAuthorship.js).
      noteAiTurnTarget(target);
      const { provider, model } = target;
      const adapter = PROVIDERS[provider];
      if (!adapter) throw new Error(`Unknown AI provider: ${provider}`);

      // The key is read (decrypted) only on the BYO path; the Apiboo path
      // never touches the vault.
      const apiKey = target.via === 'byo' ? await getAiKey(provider) : null;
      if (target.via === 'byo' && !apiKey) throw notReadyError('no_key');

      // Stream when the adapter and the runtime both allow it. Only the
      // transport differs: both paths fire the same callbacks and return the
      // same { stopReason }, so nothing below this line knows which ran.
      // An adapter with no sendStreaming, or a browser build with no Tauri
      // command to invoke, simply takes the buffered path.
      const streaming = Boolean(adapter.sendStreaming && adapter.supportsStreaming?.());

      // Tracks whether the streaming attempt produced anything the user can
      // see. It is the ONLY thing that makes the fallback below safe.
      let deliveredAnything = false;

      const args = {
        apiKey,
        model,
        system: SYSTEM_PROMPT,
        reference: CACHED_REFERENCE,
        tools: listToolSchemas(),
        messages,
        onTextDelta: (text) => {
          if (aborted) return;
          deliveredAnything = true;
          onTextDelta?.(text);
        },
        onToolUse: (block) => {
          if (aborted) return;
          deliveredAnything = true;
          onToolUse?.(block);
        },
        onUsage: (usage) => { if (!aborted) onUsage?.(usage); },
        // Streaming only. Aborting has to reach the Rust task, or a stopped
        // turn keeps generating on the user's own key.
        onStart: ({ cancel }) => { cancelStream = cancel; },
      };

      let stopReason;
      if (!streaming) {
        ({ stopReason } = await adapter.send(args));
      } else {
        try {
          ({ stopReason } = await adapter.sendStreaming(args));
        } catch (streamErr) {
          // Fall back to the buffered path when the stream produced NOTHING.
          //
          // The streaming path is new: Rust frames SSE, emits Tauri events, and
          // JS listens for them. That bridge is covered by tests on either side
          // but the seam between them is exactly the kind of wiring that fails
          // in one environment and not another — and since streaming is chosen
          // for every desktop turn, a broken bridge would mean no assistant at
          // all rather than a slower one. Buffered is the path that has been in
          // production for months.
          //
          // Safe ONLY because nothing was delivered: retrying after tokens have
          // arrived would replay half a reply and re-run any tool already
          // executed. If anything reached the user, the error stands.
          if (aborted || deliveredAnything) throw streamErr;
          console.warn('[ai] streaming failed before delivering anything — falling back to buffered:', streamErr?.message);
          cancelStream = null;
          ({ stopReason } = await adapter.send(args));
        }
      }
      if (!aborted) onEndTurn?.({ stop_reason: stopReason });
    } catch (err) {
      if (!aborted) onError?.(err?.message || 'AI request failed');
    }
  })();

  return () => {
    aborted = true;
    // Discarding the result is not enough on the streaming path: the provider
    // keeps generating, and the user is billed for all of it.
    cancelStream?.();
    cancelStream = null;
  };
}

/**
 * Prove a key works with a minimal provider call. `apiKey` may be passed
 * directly (testing a freshly-typed key before saving); otherwise the
 * stored key is used. Throws with the provider's own error message.
 */
export async function testAiKey({ provider, model, apiKey } = {}) {
  const settings = getAiSettings();
  const providerId = provider || settings.provider;
  const adapter = PROVIDERS[providerId];
  if (!adapter) throw new Error(`Unknown AI provider: ${providerId}`);
  if (providerMeta(providerId).keyless) {
    // Keyless: "test" means "is the assistant configured and reachable".
    await adapter.testKey({});
    return true;
  }
  const key = apiKey || (await getAiKey(providerId));
  if (!key) throw new Error('No API key to test.');
  await adapter.testKey({ apiKey: key, model: model || settings.model });
  return true;
}
